import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { Role } from '../../types';
import { UserPlusIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import CreateCompanyAdminModal from '../../components/admin/CreateCompanyAdminModal';

const CompanyAdminRegistration: React.FC = () => {
  const { user } = useAuth();
  const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Super Admin権限チェック
  if (!user || user.role !== Role.SUPER_ADMIN) {
    return <Navigate to='/' replace />;
  }

  const handleCreateAdminSuccess = async (adminData: { email: string; companyName: string }) => {
    setSuccessMessage(`${adminData.companyName}の管理者（${adminData.email}）を作成しました。`);
    setIsCreateAdminModalOpen(false);

    // 成功メッセージを5秒後に消す
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      {/* ヘッダー */}
      <div className='bg-white dark:bg-gray-800 shadow'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-6'>
            <div>
              <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>
                陸送会社管理者登録
              </h1>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                陸送会社の管理者アカウントを作成します
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {successMessage && (
          <div className='mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4'>
            <div className='flex items-center'>
              <CheckCircleIcon className='h-5 w-5 text-green-400 mr-2' />
              <p className='text-sm text-green-800 dark:text-green-200'>{successMessage}</p>
            </div>
          </div>
        )}

        {/* メインコンテンツ */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg'>
          <div className='px-6 py-8'>
            <div className='text-center'>
              <UserPlusIcon className='mx-auto h-12 w-12 text-gray-400 mb-4' />
              <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
                新しい陸送会社管理者を登録
              </h3>
              <p className='text-sm text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto'>
                陸送会社の管理者アカウントを作成します。作成されたアカウントは、
                その会社の従業員管理や配車業務にアクセスできるようになります。
              </p>

              <button
                onClick={() => setIsCreateAdminModalOpen(true)}
                className='inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
              >
                <UserPlusIcon className='h-5 w-5 mr-2' />
                管理者を作成
              </button>
            </div>

            {/* 注意事項 */}
            <div className='mt-12 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6'>
              <h4 className='text-sm font-medium text-blue-900 dark:text-blue-100 mb-3'>
                管理者作成時の注意事項
              </h4>
              <ul className='text-sm text-blue-800 dark:text-blue-200 space-y-2'>
                <li>• 作成されたアカウントには自動的に管理者権限が付与されます</li>
                <li>• 管理者は自社の従業員アカウントを作成・管理できます</li>
                <li>
                  • 初期パスワードは安全な場所に保管し、最初のログイン後に変更を促してください
                </li>
                <li>• メールアドレスは確認のためのメールが送信されます</li>
              </ul>
            </div>

            {/* 管理権限一覧 */}
            <div className='mt-8 bg-gray-50 dark:bg-gray-700 rounded-lg p-6'>
              <h4 className='text-sm font-medium text-gray-900 dark:text-gray-100 mb-3'>
                管理者が利用可能な機能
              </h4>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700 dark:text-gray-300'>
                <div>
                  <h5 className='font-medium mb-2'>ユーザー管理</h5>
                  <ul className='space-y-1'>
                    <li>• 配車担当者アカウント作成</li>
                    <li>• ドライバーアカウント作成</li>
                    <li>• 従業員アカウント管理</li>
                  </ul>
                </div>
                <div>
                  <h5 className='font-medium mb-2'>業務管理</h5>
                  <ul className='space-y-1'>
                    <li>• 案件管理</li>
                    <li>• 配車スケジュール管理</li>
                    <li>• レポート作成・閲覧</li>
                  </ul>
                </div>
                <div>
                  <h5 className='font-medium mb-2'>マスターデータ管理</h5>
                  <ul className='space-y-1'>
                    <li>• 取引先管理</li>
                    <li>• 車両情報管理</li>
                    <li>• 陸送員管理</li>
                  </ul>
                </div>
                <div>
                  <h5 className='font-medium mb-2'>システム設定</h5>
                  <ul className='space-y-1'>
                    <li>• 会社設定</li>
                    <li>• 通知設定</li>
                    <li>• システム設定</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* モーダル */}
        <CreateCompanyAdminModal
          isOpen={isCreateAdminModalOpen}
          onClose={() => setIsCreateAdminModalOpen(false)}
          onSuccess={handleCreateAdminSuccess}
        />
      </div>
    </div>
  );
};

export default CompanyAdminRegistration;
